/* ==============================================================================
   Tela de cadastro
   ==============================================================================
   Mesma linguagem visual do login: card claro, campos com linha inferior e
   ícone, botão em pílula.

   Decisões de comportamento, e o problema que cada uma evita:

   1. **Erro só depois da primeira tentativa de envio.** Marcar vermelho
      enquanto a pessoa digita é hostil: ela vê o erro antes de ter chance de
      terminar. O feedback POSITIVO (requisito cumprido) aparece na hora.

   2. **Requisitos de senha em lista, não barra colorida.** Barra "média/forte"
      não diz o que fazer; lista com marcação diz exatamente o que falta.

   3. **Comprimento no lugar de regra de composição.** Regra de composição
      produz `Senha@123` — previsível e curta. O piso é comprimento; a força
      real vem do Argon2id no armazenamento.

   4. **Nome de usuário normalizado ao vivo**, para o valor enviado ser o valor
      visto — sem a surpresa de "meu nome mudou depois de enviar".
   ============================================================================== */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  AtIcon,
  AuthShell,
  ErrorSlot,
  Field,
  FieldGroup,
  FinePrint,
  FooterPrompt,
  LockIcon,
  MailIcon,
  PasswordToggle,
  SubmitButton,
  UserIcon,
} from './AuthShell.tsx';

export interface RegisterScreenProps {
  onSubmit: (input: {
    email: string;
    username: string;
    password: string;
    displayName?: string;
  }) => Promise<void>;
  onLoginClick: () => void;
  error?: string | null;
  loading?: boolean;
  disabled?: boolean;
  instanceName?: string;
}

const MIN_PASSWORD = 12;

/** Espelha a validação do servidor. Divergir aqui produz um 400 que a interface
 *  não previu — o que parece bug para quem usa. */
const USERNAME_PATTERN = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/;

export function RegisterScreen({
  onSubmit,
  onLoginClick,
  error,
  loading = false,
  disabled = false,
  instanceName = 'Concord',
}: RegisterScreenProps) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const displayNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    displayNameRef.current?.focus();
  }, []);

  const blocked = loading || disabled;

  /**
   * Sugere o nome de usuário a partir do nome de exibição, e para de sugerir
   * assim que a pessoa editar o campo à mão — autopreenchimento que apaga o que
   * o usuário digitou é pior que não ter.
   */
  useEffect(() => {
    if (usernameEdited) return;
    setUsername(
      displayName
        .normalize('NFD')
        // Remove diacrítico: "João" -> "joao". O servidor só aceita [a-z0-9._-].
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '_')
        .replace(/^[._-]+|[._-]+$/g, '')
        .slice(0, 32),
    );
  }, [displayName, usernameEdited]);

  const checks = useMemo(
    () => [
      {
        id: 'len',
        label: `Pelo menos ${MIN_PASSWORD} caracteres`,
        ok: password.length >= MIN_PASSWORD,
      },
      {
        id: 'notEmail',
        label: 'Diferente do seu e-mail',
        ok: password.length > 0 && password.toLowerCase() !== email.toLowerCase(),
      },
      {
        id: 'notCommon',
        label: 'Não começa com sequência óbvia',
        // Lista curta de propósito: verificação séria de senha vazada pertence
        // ao servidor (k-anonymity contra base conhecida), não a um array no
        // cliente. Isto pega só o caso grosseiro.
        ok: password.length > 0 && !/^(?:1234|senha|password|qwerty|abcd)/i.test(password),
      },
    ],
    [password, email],
  );

  const usernameValid = username.length >= 2 && USERNAME_PATTERN.test(username);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const passwordValid = checks.every((c) => c.ok);
  const formValid = usernameValid && emailValid && passwordValid;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    setSubmitted(true);
    if (!formValid) return;
    void onSubmit({
      email,
      username,
      password,
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
    });
  };

  return (
    <AuthShell
      title="Criar conta"
      instanceName={instanceName}
      footer={
        <FooterPrompt
          text="Já tem uma conta?"
          actionLabel="Entrar"
          onAction={onLoginClick}
        />
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <FieldGroup>
          <Field
            ref={displayNameRef}
            id="displayName"
            label="Seu nome"
            type="text"
            value={displayName}
            onChange={setDisplayName}
            autoComplete="name"
            placeholder="Como quer ser chamado"
            disabled={blocked}
            icon={<UserIcon />}
          />

          <Field
            id="username"
            label="Nome de usuário"
            type="text"
            value={username}
            onChange={(v) => {
              setUsernameEdited(true);
              // Normaliza ao vivo para o mesmo conjunto que o servidor aceita.
              setUsername(v.toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 32));
            }}
            autoComplete="username"
            placeholder="usuario"
            disabled={blocked}
            required
            icon={<AtIcon />}
            invalid={submitted && !usernameValid}
            hint={
              submitted && !usernameValid
                ? 'De 2 a 32 caracteres: letras, números, ponto, hífen ou sublinhado.'
                : undefined
            }
          />

          <Field
            id="email"
            label="E-mail"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            placeholder="Digite seu e-mail"
            disabled={blocked}
            required
            icon={<MailIcon />}
            invalid={submitted && !emailValid}
            hint={submitted && !emailValid ? 'Informe um e-mail válido.' : undefined}
          />

          <div>
            <Field
              id="password"
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              placeholder="Crie uma senha"
              disabled={blocked}
              required
              icon={<LockIcon />}
              trailing={
                <PasswordToggle
                  shown={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              }
            />

            {/* Só aparece quando há algo digitado, para não receber a pessoa com
                uma lista de pendências. */}
            {password.length > 0 && (
              /* gap-1.5 (6px) entre os itens e não gap-1: a lista aparece e
                 desaparece conforme a pessoa digita, e com 4px as três linhas
                 liam como um bloco de texto em vez de itens marcáveis. */
              <ul className="mt-3 flex flex-col gap-1.5" aria-live="polite">
                {checks.map((check) => (
                  <li
                    key={check.id}
                    className="flex items-center gap-2"
                    style={{
                      fontSize: 'var(--text-2xs)',
                      lineHeight: 'var(--leading-normal)',
                      color: check.ok
                        ? 'var(--color-success)'
                        : 'var(--color-text-tertiary)',
                    }}
                  >
                    {/* Ícone além da cor: estado nunca é comunicado só por cor. */}
                    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true"
                         className="shrink-0">
                      {check.ok ? (
                        <path d="M2.5 6.4l2.3 2.3 4.7-5" stroke="currentColor"
                              strokeWidth="1.8" strokeLinecap="round"
                              strokeLinejoin="round" fill="none" />
                      ) : (
                        <circle cx="6" cy="6" r="1.8" fill="currentColor" opacity="0.45" />
                      )}
                    </svg>
                    {check.label}
                    <span className="sr-only">
                      {check.ok ? '— atendido' : '— pendente'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </FieldGroup>

        <ErrorSlot message={error} />

        <SubmitButton loading={loading} disabled={disabled}>
          {loading ? 'Criando' : 'Criar conta'}
        </SubmitButton>

        <FinePrint>
          Instância auto-hospedada: seus dados ficam neste servidor.
        </FinePrint>
      </form>
    </AuthShell>
  );
}
